import axios from 'axios';

/**
 * Platform Activity Logging
 *
 * Records user *activity* (page views and reads) to the shared platform audit
 * trail so it shows up on Management → Audit Log alongside the server-side
 * CREATE/UPDATE/DELETE mutations. Mutations are audited server-side by the
 * saf3ai-audit middleware; this fills in the read/view side.
 *
 * Design notes:
 *  - Uses its OWN axios instance (not the shared apiClient) on purpose:
 *      • no response interceptor → a failed/401 activity call never triggers
 *        the session-expiry modal, and
 *      • the apiClient's GET-read logger ignores this instance → no recursion.
 *  - Fire-and-forget + fully swallowed errors: activity logging must NEVER
 *    affect the user experience.
 *  - Identity (user/org) is derived server-side from the JWT; we only describe
 *    *what* was viewed/read.
 */

const API_ORIGIN =
  (typeof window !== 'undefined' && window.env?.REACT_APP_API_ORIGIN) ||
  process.env.REACT_APP_API_ORIGIN ||
  (typeof window !== 'undefined' && window.location?.origin) ||
  '';

const ACTIVITY_URL = '/ops/api/v1/audit-log/activity';

// Marker so the apiClient read-logger can identify and skip these requests.
export const ACTIVITY_REQUEST_MARKER = '__saf3ai_activity__';

const activityApi = axios.create({
  baseURL: API_ORIGIN,
  headers: { 'Content-Type': 'application/json' },
  timeout: 8000,
});

const getOrgId = () => {
  try {
    const user = JSON.parse(localStorage.getItem('user') || 'null');
    return user?.organization_id || user?.org_id || user?.organizationId || null;
  } catch {
    return null;
  }
};

const getToken = () =>
  localStorage.getItem('jwt_token') || localStorage.getItem('access_token');

/**
 * Record one activity event. Never throws.
 * @param {{action?:string, resourceType?:string, resourceId?:string,
 *          service?:string, method?:string, path?:string, statusCode?:number}} evt
 */
export const logActivity = (evt = {}) => {
  const token = getToken();
  if (!token) return; // not signed in — nothing to attribute

  const orgId = getOrgId();
  const params = { [ACTIVITY_REQUEST_MARKER]: 1 };
  if (orgId) params.organization_id = orgId;

  const body = {
    action: evt.action || 'ACCESS',
    resource_type: evt.resourceType,
    resource_id: evt.resourceId,
    service: evt.service,
    method: evt.method,
    path: evt.path,
    status_code: evt.statusCode,
  };

  // Fire-and-forget; swallow every error (network, 4xx, 5xx, timeout).
  activityApi
    .post(ACTIVITY_URL, body, {
      params,
      headers: { Authorization: `Bearer ${token}` },
    })
    .catch(() => {});
};

const activityService = { logActivity };

export default activityService;
